import type {
  TemplateListItemView,
  TemplatePreviewView,
  TemplateVersionView,
  TemplateView,
} from "@planerius/contracts/cabinet-notifications"
import {
  expandPlaceholders,
  lintTemplate,
} from "@planerius/contracts/notification-templates"
import type { ReactNode } from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import {
  fetchTemplate,
  fetchTemplates,
  fetchTemplateVersions,
  previewTemplate,
  resetTemplate,
  rollbackTemplate,
  saveTemplate,
  testTemplate,
} from "../../../api/notifications.api"
import type { ApiProblem } from "../../../api/problem"
import { isApiProblem } from "../../../api/problem"
import { todayInZone, viewerTimeZone } from "../../../lib/format"
import { ProblemScreen, Skeleton } from "../../../shell/states"
import { Button, EmptyState } from "../../../ui"
import { cx } from "../../../lib/cx"
// Токены заметок (внимание / беда / нейтральное) объявлены в одном файле на весь кабинет:
// два набора цветов «для беды» разошлись бы в первый же месяц.
import "../../../journal/journal-tokens.css"
import styles from "./templates.module.css"
import {
  canSave,
  costHint,
  costLine,
  costTone,
  notAllowedLine,
  notAllowedPlaceholders,
  originLine,
  promoLine,
  templateTitle,
  unknownLine,
  versionsLabel,
} from "./templates-model"

/**
 * 4-Б6 «Шаблоны сообщений» — /settings/templates.
 *
 * ЗАЧЕМ САЛОНУ ЭТОТ ЭКРАН. Текст напоминания — это голос салона: «Напоминаем: сегодня
 * в 15:00» и «Ждём вас сегодня в 15:00, не опаздывайте» читаются по-разному, и выбирать
 * должен салон. Наш текст при этом юридически вычитан, поэтому вернуться к нему можно
 * одной кнопкой — и это главная кнопка экрана, а не украшение.
 *
 * ПОДСТАНОВКИ — ВЫБОРОМ, А НЕ НАБОРОМ СКОБОК. Набирая руками, человек пишет
 * «{имя_мастера}», и текст уходит клиенту с дырой посреди предложения. Поэтому чипы
 * вставляют подстановку в место курсора, а неизвестная гасит «Сохранить» и объясняет,
 * что, скорее всего, имелось в виду.
 *
 * ЦЕНА ВИДНА ДО СОХРАНЕНИЯ И ДИАПАЗОНОМ. Салон должен узнать, что текст стал стоить
 * вдвое, в редакторе, а не из счёта в конце месяца. Диапазон — потому что провайдер
 * не выбран, и одна цифра была бы суммой, которой не будет.
 */
export function TemplatesScreen(): ReactNode {
  const timeZone = viewerTimeZone()
  const today = todayInZone(timeZone)

  const [list, setList] = useState<readonly TemplateListItemView[] | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [selected, setSelected] = useState<{ event: string; channel: string } | null>(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    void (async () => {
      try {
        const answer = await fetchTemplates(controller.signal)
        if (controller.signal.aborted) return
        setList(answer.items)
        setProblem(null)
        setSelected((current) => {
          if (current !== null) return current
          const first = answer.items[0]
          return first === undefined ? null : { event: first.event, channel: first.channel }
        })
      } catch (error) {
        if (controller.signal.aborted) return
        if (isApiProblem(error)) setProblem(error)
      }
    })()
    return () => {
      controller.abort()
    }
  }, [attempt])

  if (problem !== null && list === null) {
    return (
      <ProblemScreen
        problem={problem}
        onRetry={() => {
          setAttempt((value) => value + 1)
        }}
      />
    )
  }

  return (
    <div className={styles["screen"]}>
      <header className={styles["head"]}>
        <h1 className={styles["title"]}>Шаблоны сообщений</h1>
        <p className={styles["lead"]}>
          Это то, что прочитает ваш клиент. Наш текст вычитан юристом — если сомневаетесь
          в своём, верните его одной кнопкой.
        </p>
      </header>

      {list === null ? (
        <div className={styles["rows"]} aria-busy="true">
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} width="100%" height={56} radius={12} />
          ))}
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          title="Шаблонов пока нет"
          text="Список событий задаёт платформа. Если он пуст — напишите нам, это ошибка на нашей стороне."
        />
      ) : (
        <div className={styles["layout"]}>
          <nav className={styles["rows"]} aria-label="Шаблоны">
            {list.map((item) => {
              const active = selected?.event === item.event && selected.channel === item.channel
              return (
                <button
                  key={`${item.event}:${item.channel}`}
                  type="button"
                  className={cx(styles["row"], active && styles["rowActive"])}
                  aria-current={active ? "true" : undefined}
                  onClick={() => {
                    setSelected({ event: item.event, channel: item.channel })
                  }}
                >
                  <span className={styles["rowTitle"]}>{templateTitle(item)}</span>
                  <span className={styles["rowSub"]}>{originLine(item, today)}</span>
                </button>
              )
            })}
          </nav>

          {selected === null ? null : (
            <TemplateEditor
              key={`${selected.event}:${selected.channel}`}
              event={selected.event}
              channel={selected.channel}
              today={today}
              onSaved={() => {
                setAttempt((value) => value + 1)
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Редактор одного шаблона.
 *
 * ПРОВЕРКА ИДЁТ В ДВА ЭТАЖА, И ЭТО НЕ ДУБЛИРОВАНИЕ. Подстановки и реклама считаются
 * ЗДЕСЬ, по тем же правилам из контрактов, что и на сервере: они нужны на каждом
 * нажатии клавиши, а ходить за ними на сервер — значит показывать ошибку с опозданием
 * в полсекунды после того, как человек уже нажал «Сохранить». Цена и предпросмотр,
 * наоборот, считаются ТОЛЬКО на сервере: они зависят от самых длинных реальных значений
 * салона, и посчитать их в браузере нечем.
 */
function TemplateEditor(props: {
  readonly event: string
  readonly channel: string
  readonly today: string
  readonly onSaved: () => void
}): ReactNode {
  const [loaded, setLoaded] = useState<TemplateView | null>(null)
  const [body, setBody] = useState("")
  const [preview, setPreview] = useState<TemplatePreviewView | null>(null)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const [saving, setSaving] = useState(false)
  /**
   * Что сказать человеку после действия.
   *
   * ВИД ОБЯЗАТЕЛЕН. Раньше и «Сохранили», и отказ сервера показывались одной серой
   * строкой с role="status": неудача выглядела как успех, и салон уходил с экрана
   * уверенный, что текст сохранён. Отказ обязан читаться отказом — и цветом, и
   * role="alert", который чтец объявляет сразу.
   */
  const [note, setNote] = useState<{ readonly kind: "ok" | "bad"; readonly text: string } | null>(
    null,
  )
  const [versions, setVersions] = useState<readonly TemplateVersionView[] | null>(null)
  const area = useRef<HTMLTextAreaElement | null>(null)

  const apply = useCallback((view: TemplateView) => {
    setLoaded(view)
    setBody(view.body)
    setPreview(view.preview)
    setVersions(null)
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void (async () => {
      try {
        const view = await fetchTemplate(props.event, props.channel, controller.signal)
        if (controller.signal.aborted) return
        apply(view)
        setProblem(null)
      } catch (error) {
        if (controller.signal.aborted) return
        if (isApiProblem(error)) setProblem(error)
      }
    })()
    return () => {
      controller.abort()
    }
  }, [props.event, props.channel, apply])

  /*
   * СЧЁТ ЧАСТЕЙ И ЦЕНА ПЕРЕСПРАШИВАЮТСЯ С ЗАДЕРЖКОЙ. Запрос на каждое нажатие клавиши
   * — это сотня запросов на абзац; без задержки цена скачет и читать её невозможно.
   */
  useEffect(() => {
    if (loaded === null || body === loaded.body) return
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const answer = await previewTemplate(
            { event: props.event as never, channel: props.channel as never, body },
            controller.signal,
          )
          if (!controller.signal.aborted) setPreview(answer)
        } catch (error) {
          /*
           * ЦЕНА, ОСТАВШАЯСЯ ОТ ПРЕЖНЕГО ТЕКСТА, ХУЖЕ ОТСУТСТВИЯ ЦЕНЫ. Раньше отказ
           * предпросмотра гасился молча, и на экране висела строка стоимости выброшенного
           * черновика: салон правил текст, видел «1 часть, 3,50 ₽» и сохранял трёхчастное
           * сообщение. Теперь цена исчезает, а причина называется.
           */
          if (!controller.signal.aborted) {
            setPreview(null)
            setNote({
              kind: "bad",
              text: isApiProblem(error)
                ? (error.message ?? "Не удалось посчитать длину и цену")
                : "Не удалось посчитать длину и цену: проверьте связь.",
            })
          }
        }
      })()
    }, 500)
    return () => {
      controller.abort()
      window.clearTimeout(timer)
    }
  }, [body, loaded, props.event, props.channel])

  /*
   * МГНОВЕННАЯ ПРОВЕРКА ТЕМИ ЖЕ ПРАВИЛАМИ. Сервер откажет в сохранении по этим же
   * находкам, поэтому показать их надо ДО нажатия, а не после.
   */
  const local = useMemo(
    () => ({ unknown: expandPlaceholders(body).unknown, promo: lintTemplate(body).promo }),
    [body],
  )

  /*
   * Подстановки, которые этому событию не разрешены. Проверяется здесь по перечню,
   * ПРИШЕДШЕМУ ОТ СЕРВЕРА, поэтому экран и сервер не могут разойтись: перечень один.
   */
  const notAllowed = useMemo(
    () => (loaded === null ? [] : notAllowedPlaceholders(body, loaded.placeholders)),
    [body, loaded],
  )

  const dirty = loaded !== null && body !== loaded.body
  const shown: TemplatePreviewView | null =
    preview === null
      ? null
      : {
          ...preview,
          unknown: local.unknown.map((item) => ({ raw: item.raw, suggestion: item.suggestion })),
          promo: local.promo.map((item) => ({ quote: item.quote, why: item.why })),
        }

  const insert = (name: string): void => {
    const node = area.current
    const token = `{${name}}`
    if (node === null) {
      setBody((current) => `${current}${token}`)
      return
    }
    const from = node.selectionStart
    const to = node.selectionEnd
    setBody((current) => `${current.slice(0, from)}${token}${current.slice(to)}`)
    // Курсор остаётся за вставленной подстановкой: человек продолжает набирать фразу,
    // а не ищет, куда он попал.
    window.setTimeout(() => {
      node.focus()
      node.setSelectionRange(from + token.length, from + token.length)
    }, 0)
  }

  const run = async (action: () => Promise<TemplateView>, done: string): Promise<void> => {
    setSaving(true)
    setNote(null)
    try {
      apply(await action())
      setNote({ kind: "ok", text: done })
      props.onSaved()
    } catch (error) {
      /*
       * СНАЧАЛА ЧЕЛОВЕЧЕСКОЕ ОБЪЯСНЕНИЕ, А НЕ ЗАГОЛОВОК. `message` сервера — это готовая
       * фраза с выходом из тупика: «допишите СТОП — отказ», «сократите на 26 знаков»,
       * «возможно, вы имели в виду {мастер}». Показав вместо неё `title` («Текст слишком
       * длинный для SMS»), мы называем беду и не говорим, что делать.
       */
      setNote({
        kind: "bad",
        text: isApiProblem(error)
          ? (error.message ?? error.detail ?? error.title ?? "Не удалось сохранить")
          : "Не удалось сохранить: проверьте связь и повторите.",
      })
    } finally {
      setSaving(false)
    }
  }

  if (problem !== null && loaded === null) {
    return <div className={styles["editor"]}>{problem.message ?? "Не удалось загрузить шаблон"}</div>
  }
  if (loaded === null) {
    return (
      <div className={styles["editor"]} aria-busy="true">
        <Skeleton width="100%" height={220} radius={12} />
      </div>
    )
  }

  const versionsText = versionsLabel(loaded)

  return (
    <section className={styles["editor"]}>
      <header className={styles["editorHead"]}>
        <div>
          <div className={styles["editorTitle"]}>{templateTitle(loaded)}</div>
          <div className={styles["editorSub"]}>{originLine(loaded, props.today)}</div>
        </div>
        {/*
          * «ВЕРНУТЬ НАШ ТЕКСТ» — ГЛАВНАЯ КНОПКА ЭКРАНА, а не украшение: салон правит
          * юридически вычитанный текст, и вернуться к безопасному состоянию он должен
          * уметь мгновенно. Скрывать её, пока правок нет, незачем — она объясняет,
          * что наш текст вообще есть.
          */}
        <Button
          size="sm"
          variant="secondary"
          disabled={saving || loaded.origin === "system"}
          onClick={() => {
            void run(() => resetTemplate(props.event, props.channel), "Вернули наш текст")
          }}
        >
          Вернуть наш текст
        </Button>
        {versionsText === null || loaded.templateId === null ? null : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const id = loaded.templateId
              if (id === null) return
              if (versions !== null) {
                setVersions(null)
                return
              }
              void (async () => {
                try {
                  const answer = await fetchTemplateVersions(id)
                  setVersions(answer.items)
                } catch (error) {
                  // Кнопка, которая на отказ не делает ничего, читается как сломанная,
                  // и человек жмёт её ещё трижды.
                  setNote({
                    kind: "bad",
                    text: isApiProblem(error)
                      ? (error.message ?? "Не удалось прочитать версии")
                      : "Не удалось прочитать версии: проверьте связь.",
                  })
                }
              })()
            }}
          >
            {versionsText}
          </Button>
        )}
      </header>

      {versions === null ? null : (
        <ul className={styles["versions"]}>
          {versions.map((item) => (
            <li key={item.version} className={styles["version"]}>
              <div className={styles["versionHead"]}>
                <b>Версия {item.version}</b>
                <span className={styles["versionWho"]}>
                  {item.author ?? "наш текст"} · {item.createdAt.slice(0, 10)}
                </span>
                {item.isCurrent ? (
                  <span className={styles["versionNow"]}>сейчас работает</span>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={saving || loaded.templateId === null}
                    onClick={() => {
                      const id = loaded.templateId
                      if (id === null) return
                      void run(
                        () => rollbackTemplate(id, item.version),
                        `Вернули версию ${String(item.version)}`,
                      )
                    }}
                  >
                    Вернуть эту
                  </Button>
                )}
              </div>
              {/* Текст версии рядом с кнопкой: откат вслепую салон делать не станет. */}
              <p className={styles["versionBody"]}>{item.body}</p>
            </li>
          ))}
        </ul>
      )}

      <label className={styles["field"]}>
        <span className={styles["srOnly"]}>Текст сообщения</span>
        <textarea
          ref={area}
          className={cx(
            styles["area"],
            (local.unknown.length > 0 || notAllowed.length > 0) && styles["areaBad"],
          )}
          value={body}
          rows={4}
          maxLength={4000}
          onChange={(event) => {
            setBody(event.target.value)
          }}
        />
      </label>

      <div className={styles["subs"]}>
        <span className={styles["hint"]}>Подстановки — выбором, не набором скобок:</span>
        {loaded.placeholders.map((item) => (
          <button
            key={item.ru}
            type="button"
            className={styles["sub"]}
            title={item.title}
            onClick={() => {
              insert(item.ru)
            }}
          >
            {`{${item.ru}}`}
          </button>
        ))}
      </div>

      {shown === null ? null : (
        <>
          <div
            className={cx(
              styles["cost"],
              costTone(shown) === "warn" ? styles["costWarn"] : styles["costOk"],
            )}
          >
            <b>{costLine(shown)}</b> {costHint(shown)}
          </div>

          {shown.promo.map((item) => (
            <div key={item.quote} className={styles["promo"]} role="status">
              <b>{promoLine(item)}</b>{" "}
              <span className={styles["promoWhy"]}>{item.why}.</span>
            </div>
          ))}

          {shown.unknown.map((item) => (
            <div key={item.raw} className={styles["bad"]} role="alert">
              {unknownLine(item)}
            </div>
          ))}

          {notAllowed.length === 0 ? null : (
            <div className={styles["bad"]} role="alert">
              {notAllowedLine(notAllowed)}
            </div>
          )}

          {/*
            * ПРЕДУПРЕЖДЕНИЯ СЕРВЕРА О ЦЕНЕ ПОКАЗЫВАЮТСЯ, А НЕ ПРОПАДАЮТ. Сервер считает
            * их по самым длинным реальным значениям салона: «до следующей части осталось
            * 4 знака — любая длинная услуга удвоит цену». Без них на этом месте стояла
            * успокаивающая подсказка «вы в пределах одной части», и салон узнавал правду
            * из счёта.
            */}
          {shown.warnings.map((line) => (
            <div key={line} className={styles["promo"]} role="status">
              {line}
            </div>
          ))}

          <div className={styles["previewBox"]}>
            <span className={styles["hint"]}>Предпросмотр на реальных данных</span>
            <div className={styles["bubble"]}>{shown.rendered}</div>
          </div>
        </>
      )}

      {note === null ? null : (
        <div
          className={note.kind === "bad" ? styles["bad"] : styles["note"]}
          role={note.kind === "bad" ? "alert" : "status"}
        >
          {note.text}
        </div>
      )}

      <div className={styles["actions"]}>
        <Button
          variant="primary"
          disabled={!canSave({ preview: shown, dirty, saving, notAllowed })}
          onClick={() => {
            void run(
              () =>
                saveTemplate(props.event, props.channel, {
                  body,
                  ...(loaded.subject === null ? {} : { subject: loaded.subject }),
                  expectedUpdatedAt: loaded.updatedAt,
                }),
              "Сохранили",
            )
          }}
        >
          Сохранить
        </Button>
        <Button
          variant="secondary"
          disabled={saving}
          onClick={() => {
            void (async () => {
              setNote(null)
              try {
                const answer = await testTemplate({ channel: props.channel as never, body })
                setNote(
                  answer.accepted
                    ? { kind: "ok", text: `Отправили на ${answer.target ?? "ваш адрес"}` }
                    : { kind: "bad", text: answer.reason ?? "Тест не ушёл" },
                )
              } catch (error) {
                setNote({
                  kind: "bad",
                  text: isApiProblem(error)
                    ? (error.message ?? "Тест не ушёл")
                    : "Тест не ушёл: проверьте связь.",
                })
              }
            })()
          }}
        >
          Отправить себе тест
        </Button>
      </div>
    </section>
  )
}
